import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  DestroyRef,
  effect,
  inject,
  Injector,
  viewChild,
  ViewContainerRef,
  type OutputRef,
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';

import { CadsMainTemplateModalComponent } from '../components/cads-main-template-modal/cads-main-template-modal.component';
import { CadsIconViewBitComponent } from '../bits/cads-icon-view-bit/cads-icon-view-bit.component';

import type { CadsGlobalModalRequest } from './cads-global-modal.config';
import { CadsGlobalModalService } from './cads-global-modal.service';

/**
 * Single app-level outlet for {@link CadsGlobalModalService}. Mount once in the shell (e.g. desktop
 * `app.component.html` next to the legacy global modal host).
 */
@Component({
  selector: 'cads-global-modal-host',
  imports: [CadsMainTemplateModalComponent, CadsIconViewBitComponent, NgTemplateOutlet],
  templateUrl: './cads-global-modal-host.component.html',
  styleUrl: './cads-global-modal-host.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CadsGlobalModalHostComponent {
  private readonly _modalService = inject(CadsGlobalModalService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _injector = inject(Injector);
  private readonly _bodyHost = viewChild('bodyHost', { read: ViewContainerRef });

  private _bodyRef: ComponentRef<unknown> | null = null;
  private _initializedConfirmationState: { checked: boolean } | null = null;

  readonly request = this._modalService.request;

  constructor() {
    this._modalService.registerHost();

    effect(
      () => {
        const req = this._modalService.request();
        if (!req) {
          this._initializedConfirmationState = null;
        } else if (req.simpleBody?.confirmation && req.confirmationState) {
          if (req.confirmationState !== this._initializedConfirmationState) {
            req.confirmationState.checked = req.simpleBody.confirmation.initiallyChecked ?? false;
            this._initializedConfirmationState = req.confirmationState;
          }
        } else {
          this._initializedConfirmationState = null;
        }
        const host = this._bodyHost();
        this._clearBody();
        if (!req?.component || req.bodyTemplateRef || req.simpleBody || !host) {
          return;
        }
        this._mountComponent(req, host);
      },
      { injector: this._injector },
    );

    this._destroyRef.onDestroy(() => {
      this._clearBody();
    });
  }

  protected _onDismissed(req: CadsGlobalModalRequest): void {
    req.dismissed?.();
    this._modalService.close();
  }

  protected _onSecondary(req: CadsGlobalModalRequest): void {
    req.secondaryTapped?.();
  }

  protected _onPrimary(req: CadsGlobalModalRequest): void {
    req.primaryTapped?.();
  }

  protected _onConfirmationChange(req: CadsGlobalModalRequest, ev: Event): void {
    const el = ev.target as HTMLInputElement | null;
    if (!el || !req.confirmationState) {
      return;
    }
    req.confirmationState.checked = el.checked;
  }

  private _mountComponent(req: CadsGlobalModalRequest, host: ViewContainerRef): void {
    const ref = host.createComponent(req.component!);
    for (const [key, value] of Object.entries(req.inputs ?? {})) {
      ref.setInput(key, value);
    }
    this._wireOutputs(ref, req.outputs);
    ref.changeDetectorRef.detectChanges();
    this._bodyRef = ref;
  }

  private _wireOutputs(
    ref: ComponentRef<unknown>,
    outputs: CadsGlobalModalRequest['outputs'],
  ): void {
    if (!outputs) {
      return;
    }
    const instance = ref.instance as Record<string, unknown>;
    const subs: Subscription[] = [];
    for (const [name, handler] of Object.entries(outputs)) {
      const outputRef = instance[name];
      if (outputRef == null || typeof outputRef !== 'object' || !('subscribe' in outputRef)) {
        continue;
      }
      subs.push(
        outputToObservable(outputRef as OutputRef<unknown>).subscribe((value) => handler(value)),
      );
    }
    ref.onDestroy(() => {
      for (const sub of subs) {
        sub.unsubscribe();
      }
    });
  }

  private _clearBody(): void {
    this._bodyRef?.destroy();
    this._bodyRef = null;
    this._bodyHost()?.clear();
  }
}
